import { useQuery } from 'react-query'
import { ExternalIntegrationsClient } from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { PostTag } from 'types'

class SearchUsersError extends Error {
  errorType?: string

  constructor(message: string, errorType?: string) {
    super(message)
    this.errorType = errorType
  }
}

interface UseSearchUsersProps {
  integrationId: number | undefined
  query: string
}

export const useSearchUsers = ({ integrationId, query }: UseSearchUsersProps) => {
  const workspaceId = useCurrentWorkspaceId()

  const result = useQuery<PostTag[], SearchUsersError>(
    ['searchUsers', workspaceId, integrationId, query],
    async () => {
      if (!integrationId) return []

      const response = await ExternalIntegrationsClient.searchUsers({
        workspaceId,
        integrationId,
        query,
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new SearchUsersError(
          response.data?.error || 'Failed to search users',
          response.data?.error_type,
        )
      }

      return response.data.users
    },
    {
      enabled: !!workspaceId && !!integrationId && query.length >= 3,
      cacheTime: 1000 * 60 * 5,
      keepPreviousData: true,
      retry: false,
    },
  )

  return {
    ...result,
    isAuthError: result.error?.errorType === 'authentication_error',
  }
}
